/**
 * #725 — subscribe a teaching surface to the SIGNED-IN trader's key mapping.
 *
 * Any control that names a key reads it through here, so a remap updates the
 * chip, the readout, the tooltips, and the receipts in the same render as the
 * setting itself — "visible before the trader returns to the trading surface"
 * without a reload.
 *
 * Every read is ownership-gated against the current auth user, so a mapping
 * belonging to a previously signed-in trader can never reach the screen or the
 * keyboard; it degrades to the shipped defaults instead. See
 * `stores/liveHotkeys`.
 *
 * `hooks/` has no barrel; import directly.
 */
import { useAuthStore } from '@/stores/authStore';
import { hasConfirmedGrant } from '@/stores/grants';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useOneClickStore } from '@/stores/oneClickStore';
import {
  twoSidedMultiplier,
  twoSidedOptedIn,
  type TwoSidedMultiplierResolution,
} from '@/lib/trading/twoSidedOptIn';
import { useLiveHotkeysStore, selectOwnedHotkeys } from '@/stores/liveHotkeys';
import {
  hotkeyLabelFor,
  hotkeyPairLabel,
  isActionUnbound,
  liveKeyList,
  shiftTwoSidedChordLabel,
  type LiveHotkeyActionId,
  type LiveHotkeyGrants,
  type LiveHotkeyMap,
} from '@/lib/trading/liveHotkeyRegistry';

/**
 * The viewer's grants, subscribed so a teaching surface stops naming a key the
 * moment the capability behind it goes away. Resolved HERE rather than at each
 * call site for the same reason the ownership gate is: a second copy of this
 * rule is a second copy to get wrong.
 */
export function useLiveHotkeyGrants(): LiveHotkeyGrants {
  // D38 — CONFIRMED only, as the listener reads it: a restored grant this
  // session has not confirmed fires nothing, so no surface may name W/E.
  const attack = useAuthStore((s) => hasConfirmedGrant(s.user, 'attackAccess'));
  // #724 — the trader's own opt-in rides with the grants so a teaching
  // surface names S only while S actually fires — for THIS trader.
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  const twoSided = useDisplaySettings((s) => twoSidedOptedIn(s, viewerId));
  return { attack, twoSided };
}

/** The viewer's own mapping, for a surface naming more than one key. */
export function useLiveHotkeyMap(): LiveHotkeyMap {
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  return useLiveHotkeysStore((s) => selectOwnedHotkeys(s, viewerId));
}

/** One action's key, ready to render: "A", "Spacebar", or "—" when unbound. */
export function useHotkeyLabel(id: LiveHotkeyActionId): string {
  return hotkeyLabelFor(useLiveHotkeyMap(), id, useLiveHotkeyGrants());
}

/**
 * Is this action active for the viewer but reachable by no key (#740)?
 *
 * A surface that TEACHES a key asks this to explain the em dash
 * `useHotkeyLabel` returns; an ungranted or dormant action answers false, so
 * the explanation can never leak a capability the trader does not hold.
 */
export function useHotkeyUnbound(id: LiveHotkeyActionId): boolean {
  return isActionUnbound(useLiveHotkeyMap(), id, useLiveHotkeyGrants());
}

/** Two actions as the product says them, e.g. "A/D" or "W/E". */
export function useHotkeyPairLabel(
  first: LiveHotkeyActionId,
  second: LiveHotkeyActionId,
): string {
  return hotkeyPairLabel(useLiveHotkeyMap(), first, second, useLiveHotkeyGrants());
}

/**
 * The mapping for a NON-REACT reader (the key listener), gated the same way.
 *
 * Kept beside the hooks so the ownership rule has one home: a second gate
 * written at a call site is a second gate to get wrong.
 */
export function readOwnedHotkeys(): LiveHotkeyMap {
  const viewerId = useAuthStore.getState().user?.id ?? null;
  return selectOwnedHotkeys(useLiveHotkeysStore.getState(), viewerId);
}

/**
 * #1067 — the Shift+ chord on the viewer's two-sided key, e.g. "⇧S", or null
 * when the chord does not exist for them (S off or unbound, or on a key that
 * cannot carry Shift). Every surface naming the chord reads it here.
 */
export function useShiftTwoSidedChordLabel(): string | null {
  return shiftTwoSidedChordLabel(useLiveHotkeyMap(), useLiveHotkeyGrants());
}

/**
 * #1067 — the viewer's Shift+S multiplier, or why there is none. Subscribes
 * to the primitive fields and resolves outside the selector, so a render
 * never sees a fresh object identity from the store.
 */
export function useTwoSidedMultiplier(): TwoSidedMultiplierResolution {
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  const enabled = useDisplaySettings((s) => s.lt2TwoSidedEnabled);
  const ownerId = useDisplaySettings((s) => s.lt2TwoSidedOwnerId);
  const multiplier = useDisplaySettings((s) => s.lt2TwoSidedMultiplier);
  const multiplierOwnerId = useDisplaySettings((s) => s.lt2TwoSidedMultiplierOwnerId);
  return twoSidedMultiplier(
    {
      lt2TwoSidedEnabled: enabled,
      lt2TwoSidedOwnerId: ownerId,
      lt2TwoSidedMultiplier: multiplier,
      lt2TwoSidedMultiplierOwnerId: multiplierOwnerId,
    },
    viewerId,
  );
}

/**
 * The LT2 key list quick reference for the viewer: every key they can press
 * right now (see `liveKeyList`), for the readout's help trigger and the ⌨
 * chips. Follows remaps, grants, the Pull All opt-in, the Shift+S multiplier
 * and keys the desk turned off (D15).
 */
export function useLiveKeyList(): string {
  const map = useLiveHotkeyMap();
  const grants = useLiveHotkeyGrants();
  const pullAll = useDisplaySettings((s) => s.lt2PullAllEnabled);
  const multiplier = useTwoSidedMultiplier();
  const twoSidedOff = useOneClickStore((s) => s.deskOff.twoSided);
  const pullAllOff = useOneClickStore((s) => s.deskOff.pullAll);
  return liveKeyList(map, grants, {
    pullAll,
    shiftMultiplier: multiplier.ok ? multiplier.multiplier : null,
    deskOff: { twoSided: twoSidedOff, pullAll: pullAllOff },
  });
}
